/**
 * useAdVisibility - Hook to determine if ads should be shown based on:
 * 1. User's membership level (free, paid, premium)
 * 2. Club's ad policy settings
 * 3. Privacy consent
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function useAdVisibility(clubId = null) {
  const { user } = useAuth();
  const [adPolicy, setAdPolicy] = useState({
    show_ads_to_paid_members: false,
    show_sponsor_ads: true,
    ad_free_for_premium: true
  });
  const [loading, setLoading] = useState(true);

  // Fetch club's ad policy
  useEffect(() => {
    const fetchAdPolicy = async () => {
      if (!clubId) {
        // Use default/global policy
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/clubs/${clubId}`);
        if (response.ok) {
          const club = await response.json();
          if (club.ad_policy) {
            setAdPolicy(prev => ({ ...prev, ...club.ad_policy }));
          }
        }
      } catch (error) {
        console.debug('Failed to fetch club ad policy:', error);
      }
      setLoading(false);
    };

    fetchAdPolicy();
  }, [clubId]);

  // Determine user's membership level
  const memberClass = user?.membership_tier || user?.member_class || 'guest';
  const isPaid = ['paid', 'full'].includes(memberClass);
  const isPremium = memberClass === 'premium';
  const isGuest = !user || memberClass === 'guest' || memberClass === 'free';

  // Calculate visibility
  const shouldShowAds = () => {
    // Premium members never see ads if ad_free_for_premium is true
    if (isPremium && adPolicy.ad_free_for_premium) {
      return false;
    }

    // Paid members see ads only if show_ads_to_paid_members is true
    if (isPaid && !adPolicy.show_ads_to_paid_members) {
      return false;
    }

    // Everyone else sees ads
    return true;
  };

  const shouldShowSponsorAds = () => {
    if (!shouldShowAds()) return false;
    return adPolicy.show_sponsor_ads;
  };

  const shouldShowGoogleAds = () => {
    if (!shouldShowAds()) return false;
    // Google ads only for guests (non-members)
    return isGuest;
  };

  return {
    loading,
    adPolicy,
    memberClass,
    isPaid,
    isPremium,
    isGuest,
    shouldShowAds: shouldShowAds(),
    shouldShowSponsorAds: shouldShowSponsorAds(),
    shouldShowGoogleAds: shouldShowGoogleAds()
  };
}

export default useAdVisibility;
